import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import BackLink from '../components/BackLink';
import PageHeader from '../components/PageHeader';
import { Alert } from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import PageLayout from '../components/ui/PageLayout';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import SegTrack from '../components/ui/SegTrack';
import Select from '../components/ui/Select';
import Skeleton from '../components/ui/Skeleton';
import Tooltip from '../components/ui/Tooltip';
import { IconCalendar, IconClock, IconPencil, IconSpark } from '../components/ui/icons';
import { CONTROL_MOTION, FOCUS, STATE } from '../components/ui/motion';
import MessageThread from '../components/MessageThread/MessageThread';
import { useToast } from '../components/Toast/ToastContext';
import { liveScope, useLiveScopes } from '../live/useLiveScopes';
import { getMyWork, getProduction, listPeople, updateProduction } from './sparkApi';
import type { DateOffer, Person, ProductionDetail, ProductionPatch, Task, TimeEntry } from './sparkApi';
import SparkDeliverables from './SparkDeliverables';
import { SparkEventPlate } from './SparkEventLinks';
import SparkPyroPanel from './SparkPyroPanel';
import SparkTeamPanel from './SparkTeamPanel';
import { ReviewStatusText, ReviewStrip, SpiceMeter, StageTag, StatusTag } from './SparkBits';
import { InlineText, PropGroup, PropRow } from '../components/ui/PropRail';
import { DateValue, DroneCounts, SpiceRadio, SpiceReadOnly, whenText } from './SparkProps';
import { CHANNELS, PIPELINE, RUNWAYS, STATUS_REASONS } from './sparkRules';
import type { Channel, Runway, Stage } from './sparkRules';
import { dayText, errorMessage, statusOf, useSparkAccess } from './sparkUi';

const RUNWAY_SHORT: Partial<Record<Runway, string>> = { long: 'Long Form', standard: 'Standard', rush: 'Pop Up / Rush' };

/** The page title as a button that becomes an input: Enter or blur saves, Escape cancels. */
const EditableTitle: React.FC<{ name: string; onSave: (name: string) => void }> = ({ name, onSave }) => {
  const [editing, setEditing] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const settled = useRef(false);
  const giveBack = useRef(false);

  useEffect(() => {
    if (editing) { settled.current = false; input.current?.focus(); input.current?.select(); }
    else if (giveBack.current) { giveBack.current = false; button.current?.focus(); }
  }, [editing]);

  const finish = (save: boolean, refocus: boolean) => {
    if (settled.current) return;
    settled.current = true;
    const v = (input.current?.value ?? '').trim();
    giveBack.current = refocus;
    setEditing(false);
    if (save && v && v !== name) onSave(v);
  };

  if (editing) {
    return (
      <input
        ref={input}
        aria-label="Project name"
        defaultValue={name}
        maxLength={200}
        onBlur={() => finish(true, false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') finish(true, true);
          else if (e.key === 'Escape') finish(false, true);
        }}
        className="m-0 -mx-2 w-full min-w-[12rem] max-w-none rounded-control border-0 bg-surface-container-high px-2 py-0 text-2xl font-bold leading-tight tracking-tight text-on-surface shadow-recess focus:bg-surface-container-highest focus:outline-none focus:ring-2 focus:ring-primary/50 sm:text-3xl"
      />
    );
  }
  return (
    <Tooltip label="Rename the project" side="bottom">
      <button
        ref={button}
        type="button"
        aria-label={`${name}, rename`}
        onClick={() => setEditing(true)}
        className={`group -mx-2 inline-flex items-center gap-2 rounded-control px-2 text-left ${STATE} ${CONTROL_MOTION} ${FOCUS}`}
      >
        <span className="text-balance">{name}</span>
        <IconPencil className="h-4 w-4 flex-none text-on-surface-variant opacity-60 transition-opacity duration-quick ease-emphasized group-hover:opacity-100" />
      </button>
    </Tooltip>
  );
};

/**
 * What matters most about the show, beside its title: where it is in the pipeline, what
 * blocks it, and how close the dates are. On a phone this strip is the whole summary.
 */
const HeaderFacts: React.FC<{ detail: ProductionDetail }> = ({ detail }) => {
  const p = detail.production;
  const showDate = p.from_event ? p.show_date : p.own_show_date;
  const open = !p.archived_at && !p.flown;
  const word = p.channel === 'shows' ? 'Show' : 'Due';
  const status = statusOf(p);

  const show = showDate ? whenText(showDate, open) : null;
  const preview = p.preview_due ? whenText(p.preview_due, open) : null;

  return (
    <ul aria-label="Project summary" className="m-0 flex list-none flex-wrap items-center gap-x-4 gap-y-2 p-0">
      <Appear as="li" show={open} className="flex"><StageTag stage={p.stage} /></Appear>
      <Appear as="li" show={!!status} className="flex"><StatusTag row={p} /></Appear>
      {p.is_standing ? (
        <li className="text-sm text-on-surface-variant">Ongoing. No event and no end date.</li>
      ) : (
        <>
          {/* A linked event's plate already counts down to the show; this carries only a project's own date. */}
          {!p.from_event && (
          <li className="flex items-center gap-1.5 text-sm tabular-nums">
            <IconCalendar className="h-4 w-4 flex-none text-on-surface-variant" />
            {showDate && show ? (
              <span className="text-on-surface">
                {word} {dayText(showDate, 'MMM D')}
                <span className="text-on-surface-variant">, {show.text}</span>
              </span>
            ) : (
              <span className="text-on-surface-variant">No {word.toLowerCase()} date yet</span>
            )}
          </li>
          )}
          <Appear
            as="li"
            show={!!(p.preview_due && preview)}
            className={`flex items-center gap-1.5 text-sm tabular-nums ${
              preview?.overdue ? '-mx-0.5 rounded-control bg-red-500/15 px-2 py-0.5 font-medium text-red-300' : ''
            }`}
          >
            {p.preview_due && preview && (
              <>
                <IconClock className={`h-4 w-4 flex-none ${preview.overdue ? 'text-red-300' : 'text-on-surface-variant'}`} />
                {preview.overdue ? (
                  <span>Preview {preview.text}</span>
                ) : (
                  <span className="text-on-surface">
                    Preview {dayText(p.preview_due, 'MMM D')}
                    <span className="text-on-surface-variant">, {preview.text}</span>
                  </span>
                )}
              </>
            )}
          </Appear>
        </>
      )}
      <li className="flex"><SpiceMeter level={p.spice} /></li>
    </ul>
  );
};

const SparkProjectPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const history = useHistory();
  const location = useLocation<{ offer?: DateOffer | null } | undefined>();
  const { studio, approver, userId } = useSparkAccess();

  const [detail, setDetail] = useState<ProductionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [running, setRunning] = useState<TimeEntry | null>(null);
  const [offer, setOffer] = useState<DateOffer | null>(location.state?.offer || null);

  // Only the newest getProduction answer lands: a live refetch sent before a save's reload can arrive after it.
  const latest = useRef(0);
  const load = useCallback(() => {
    const n = ++latest.current;
    return getProduction(id)
      .then((d) => { if (latest.current === n) { setDetail(d); setError(null); } })
      .catch((e) => { if (latest.current === n) setError(errorMessage(e, 'Could not load this project.')); });
  }, [id]);

  useEffect(() => {
    setDetail(null);
    load();
  }, [load]);

  // Someone else's change: refetch in place, no skeleton, and never over a write of ours in flight.
  // A failure leaves the page as it is; the rejection tells the poller to try again.
  const shownId = useRef(id);
  shownId.current = id;
  const { holdWhile } = useLiveScopes([liveScope.sparkProject(id)], (_changed, { stale }) => {
    const asked = id;
    const n = ++latest.current;
    return getProduction(asked)
      .then((d) => { if (latest.current === n && shownId.current === asked && !stale()) { setDetail(d); setError(null); } });
  });

  const onTeam = !!detail?.team.some((m) => m.user_id === userId);
  const needPeople = studio || onTeam || approver;
  useEffect(() => {
    if (!needPeople) return;
    listPeople().then((r) => setPeople(r.people)).catch(() => undefined);
  }, [needPeople]);

  useEffect(() => {
    if (!studio) return;
    getMyWork().then((r) => setRunning(r.running)).catch(() => undefined);
  }, [studio]);

  const onTaskUpdated = useCallback((task: Task) => {
    setDetail((d) => (d ? { ...d, tasks: d.tasks.map((t) => (t.id === task.id ? task : t)) } : d));
  }, []);

  const patch = async (body: ProductionPatch, local: Partial<ProductionDetail['production']>) => {
    if (!detail) return;
    setDetail({ ...detail, production: { ...detail.production, ...local } });
    try {
      await holdWhile(updateProduction(detail.production.id, body));
      load();
    } catch (e) {
      toast.error(errorMessage(e, 'Could not save that change. Try again.'));
      load();
    }
  };

  // Bottom room so the last control can always scroll clear of Flerpie's avatar (bottom-left).
  const CLEAR_FAB = 'pb-32';

  if (error && !detail) {
    return (
      <PageLayout width="full" kind="workspace" className={`pt-6 ${CLEAR_FAB}`}>
        <Alert tone="error" onRetry={load}>{`${error} It may have been removed, or you may not have access.`}</Alert>
        <BackLink to="/spark" label="Spark" className="mt-4" />
      </PageLayout>
    );
  }

  if (!detail) {
    return (
      <div aria-busy="true" aria-label="Loading project">
        <PageLayout width="full"
          kind="record"
          className={CLEAR_FAB}
          header={(
            <div className="pb-5 pt-6">
              <Skeleton className="h-9 w-72 max-w-full" />
              <Skeleton className="mt-3 h-5 w-96 max-w-full" />
            </div>
          )}
          rail={<><Skeleton className="h-72" /><Skeleton className="h-48" /></>}
        >
          <Skeleton className="h-80" />
          <Skeleton className="h-28" />
        </PageLayout>
      </div>
    );
  }

  const p = detail.production;
  const editable = studio && !p.archived_at && !p.is_standing;
  const showDate = p.from_event ? p.show_date : p.own_show_date;
  const isShowsChannel = p.channel === 'shows';
  const dateLabel = isShowsChannel ? 'Show date' : 'Due date';
  const status = statusOf(p);
  const open = !p.archived_at && !p.flown;
  const linkedEventId = detail.events[0]?.id;

  const nothingBlocking = <span className="text-sm text-on-surface-variant">No status</span>;

  const header = (
    <div className="pb-5">
      <PageHeader
        title={editable ? <EditableTitle name={p.name} onSave={(name) => patch({ name }, { name })} /> : p.name}
        icon={<IconSpark />}
        back={{ to: '/spark', label: 'Spark' }}
        meta={<HeaderFacts detail={detail} />}
      />
      <SparkEventPlate
        productionId={p.id}
        events={detail.events}
        isStanding={p.is_standing}
        studio={studio}
        offer={offer}
        onOfferChange={(o) => { setOffer(o); if (location.state?.offer) history.replace({ ...location, state: undefined }); }}
        onChanged={load}
      />
      <Reveal show={!!p.archived_at || !!p.flown}>
        <Alert tone="info" live="status" icon={false} className="mt-4">
          {p.archived_at
            ? p.archived_reason === 'event_cancelled'
              ? 'The event was cancelled, so this project is archived. Anything flagged Stock moved to Stock Building.'
              : 'This project is archived.'
            : 'Flown. This project left the board when the show night passed.'}
        </Alert>
      </Reveal>
    </div>
  );

  const rail = (
    <>
      <Panel title="Details">
        <div className="space-y-5">
          <PropGroup title="Progress">
            <PropRow label="Pipeline">
              {editable ? (
                <Select
                  variant="inline"
                  label="Pipeline"
                  options={PIPELINE.map((s) => ({ key: s.key, label: s.label, hex: s.color }))}
                  value={p.stage}
                  onChange={(k) => patch({ stage: k as Stage }, { stage: k as Stage })}
                />
              ) : <StageTag stage={p.stage} />}
            </PropRow>
            <PropRow label="Status">
              {editable ? (
                <Select
                  variant="inline"
                  label="Status"
                  options={[{ key: '', label: 'No status' }, ...STATUS_REASONS.map((s) => ({ key: s.label, label: s.label, hex: s.color }))]}
                  value={p.status_reason || ''}
                  onChange={(k) => patch({ status_reason: k || null }, { status_reason: k || null })}
                  renderValue={() => (status ? <StatusTag row={p} /> : nothingBlocking)}
                />
              ) : status ? <StatusTag row={p} /> : nothingBlocking}
            </PropRow>
            <PropRow label="Review Status" wide>
              <Appear show={p.review.steps.length > 0} className="inline-flex">
                <ReviewStrip review={p.review} chainWhenUnsent />
              </Appear>
              <ReviewStatusText state={p.review.state} />
            </PropRow>
          </PropGroup>

          <PropGroup title="Show">
            {p.is_standing ? (
              <PropRow label="Due"><span className="text-sm text-on-surface">Ongoing</span></PropRow>
            ) : (
              <>
                <PropRow label={dateLabel}>
                  <DateValue
                    label={dateLabel}
                    value={showDate || null}
                    open={open}
                    editable={editable && !p.from_event}
                    eventId={p.from_event ? linkedEventId : undefined}
                    onSave={(v) => patch({ show_date: v }, { own_show_date: v })}
                  />
                </PropRow>
                <PropRow label="Preview due">
                  <DateValue
                    label="Preview due"
                    value={p.preview_due || null}
                    open={open}
                    editable={editable}
                    note="A starting point. Move it on any project."
                    onSave={(v) => patch({ preview_due: v }, { preview_due: v })}
                  />
                </PropRow>
              </>
            )}
            <Reveal show={isShowsChannel}>
              <PropRow label="Runway" wide>
                {editable ? (
                  <SegTrack
                    fill
                    size="sm"
                    label="Runway"
                    value={p.runway || null}
                    options={RUNWAYS.map((r) => ({ key: r.key, label: RUNWAY_SHORT[r.key] || r.label, sub: `${r.days}d`, aria: `${r.label}, ${r.days} days` }))}
                    onChange={(k) => patch({ runway: k as Runway }, { runway: k as Runway })}
                    onClear={() => patch({ runway: null }, { runway: null })}
                  />
                ) : <span className="text-sm text-on-surface">{RUNWAYS.find((r) => r.key === p.runway)?.label || 'Not set'}</span>}
              </PropRow>
            </Reveal>
            <PropRow label="Spice">
              {editable
                ? <SpiceRadio level={p.spice} onChange={(n) => patch({ spice: n }, { spice: n })} />
                : <SpiceReadOnly level={p.spice} />}
            </PropRow>
            {!p.is_standing && (
              <PropRow label="Drones">
                <DroneCounts
                  ls={p.ls_drone_count}
                  pyro={p.pyro_drone_count}
                  editable={editable && !p.from_event}
                  eventId={p.from_event ? linkedEventId : undefined}
                  onSave={(body) => patch(body, body)}
                />
              </PropRow>
            )}
            <Reveal show={p.speciality === 'systems'}>
              <PropRow label="Client">
                {editable ? (
                  <InlineText
                    label="Client"
                    value={p.client_name || ''}
                    maxLength={200}
                    placeholder="Who is it for?"
                    onSave={(v) => patch({ client_name: v || null }, { client_name: v || null })}
                  />
                ) : <span className="truncate text-sm text-on-surface">{p.client_name || 'Not set'}</span>}
              </PropRow>
            </Reveal>
          </PropGroup>

          {!p.is_standing && (
            <PropRow label="Channel" wide>
              {editable ? (
                <SegTrack
                  fill
                  size="sm"
                  label="Channel"
                  value={p.channel}
                  options={CHANNELS.map((c) => ({ key: c.key, label: c.label }))}
                  onChange={(k) => patch({ channel: k as Channel }, { channel: k as Channel })}
                />
              ) : <span className="text-sm text-on-surface">{CHANNELS.find((c) => c.key === p.channel)?.label}</span>}
            </PropRow>
          )}
        </div>
      </Panel>

      <SparkTeamPanel detail={detail} people={people} studio={studio} approver={approver} userId={userId} onChanged={load} />


    </>
  );

  return (
    <PageLayout width="full"
      kind="record"
      className={CLEAR_FAB}
      header={header}
      rail={rail}
      // The thread draws its own Activity panel on desktop and becomes a floating button on phones.
      aside={<MessageThread entityType="creative_production" entityId={p.id} />}
    >
      <SparkDeliverables
        detail={detail}
        people={people}
        studio={studio}
        approver={approver}
        archived={!!p.archived_at}
        userId={userId}
        running={running}
        onRunning={setRunning}
        onTaskUpdated={onTaskUpdated}
        onChanged={load}
      />
      {!p.is_standing && <SparkPyroPanel detail={detail} studio={editable} onChanged={load} />}
    </PageLayout>
  );
};

export default SparkProjectPage;
